/**
 * The three design stages, and what survives once the order has been exported.
 *
 * During design the app keeps per-step and per-tool undo material (mesh
 * checkpoints, pre-apply "before" meshes, operation logs). That granularity is
 * only useful while the design is being worked on. After the final export the
 * order is a delivered artefact, so everything collapses to **one retained mesh
 * per stage** — the mesh as it stood when the stage was completed.
 *
 * Two consequences, both deliberate:
 *
 *  1. Storage: every per-tool before-mesh and every intermediate step checkpoint
 *     is pruned (see `undo/stagePrune.js`). Three meshes remain instead of ~30.
 *  2. Navigation: with no per-step material left, "Previous" can no longer land
 *     mid-stage. It lands on the **first step of the previous stage**, which is
 *     the coarsest point we can still reconstruct faithfully.
 *
 * FINALIZATION is not a retained stage — it produces the export, it does not
 * hold a distinct socket of its own.
 */
import {
  WORKFLOW_TYPES,
  SCAN_PROCESS_FLOW_STEPS,
  SCAN_PROCESS_STEPS,
  CLINICAL_MODIFICATION_STEPS,
  MAIN_PROCESS_STEPS,
  GLOBAL_STEP_ORDER,
} from './flowConfig';

/** Ordered stage ids. Index order is meaningful (previous/next stage). */
export const DESIGN_STAGES = [
  WORKFLOW_TYPES.SCAN_PROCESS,
  WORKFLOW_TYPES.CLINICAL_MODIFICATION,
  WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING,
];

/**
 * Steps belonging to each stage, in flow order.
 *
 * ALIGNMENT_AND_LOCKING is spelled out rather than imported because
 * `ALIGNMENT_AND_LOCKING_STEPS` in flowConfig is a *material appearance* list
 * (which steps render the socket grey), not a flow definition — reusing it here
 * would couple stage membership to a styling decision.
 */
export const STEPS_BY_STAGE = {
  [WORKFLOW_TYPES.SCAN_PROCESS]: [...SCAN_PROCESS_FLOW_STEPS],
  [WORKFLOW_TYPES.CLINICAL_MODIFICATION]: [...CLINICAL_MODIFICATION_STEPS],
  [WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING]: [
    MAIN_PROCESS_STEPS.LOCKING_MECHANISM,
    MAIN_PROCESS_STEPS.VALVE,
    MAIN_PROCESS_STEPS.LOGO_PLACEMENT,
  ],
};

/**
 * The step whose checkpoint *is* the stage's retained mesh after export: the
 * last step of the stage, i.e. the socket as the stage left it.
 */
export const STAGE_CHECKPOINT_STEP = DESIGN_STAGES.reduce((acc, stage) => {
  const steps = STEPS_BY_STAGE[stage] ?? [];
  acc[stage] = steps[steps.length - 1] ?? null;
  return acc;
}, {});

/** Every step that keeps a checkpoint after export. */
export const RETAINED_STEPS_AFTER_EXPORT = Object.freeze(
  DESIGN_STAGES.map((stage) => STAGE_CHECKPOINT_STEP[stage]).filter(Boolean),
);

/**
 * @param {string} stepId
 * @returns {string | null} stage id, or null for FINALIZATION / unknown steps.
 */
export function stageForStep(stepId) {
  for (const stage of DESIGN_STAGES) {
    if ((STEPS_BY_STAGE[stage] ?? []).includes(stepId)) return stage;
  }
  return null;
}

/** @param {string} stageId */
export function firstStepOfStage(stageId) {
  return STEPS_BY_STAGE[stageId]?.[0] ?? null;
}

/** @param {string} stageId */
export function lastStepOfStage(stageId) {
  const steps = STEPS_BY_STAGE[stageId] ?? [];
  return steps[steps.length - 1] ?? null;
}

/**
 * Stage immediately before `stageId`, or null when already at the first stage.
 * @param {string} stageId
 */
export function previousStage(stageId) {
  const idx = DESIGN_STAGES.indexOf(stageId);
  return idx > 0 ? DESIGN_STAGES[idx - 1] : null;
}

/**
 * Backward-navigation target once the design has been exported and pruned.
 *
 * Mid-stage steps collapse to the start of their own stage (the finest point we
 * can still rebuild); the first step of a stage falls back to the start of the
 * previous stage. FINALIZATION hands back to the start of the last design stage.
 *
 * @param {string} currentStep
 * @returns {string | null}
 */
export function resolvePrunedPreviousStep(currentStep) {
  if (currentStep === MAIN_PROCESS_STEPS.FINALIZATION) {
    return firstStepOfStage(DESIGN_STAGES[DESIGN_STAGES.length - 1]);
  }

  const stage = stageForStep(currentStep);
  if (!stage) return null;

  const first = firstStepOfStage(stage);
  if (currentStep !== first) return first;

  const prev = previousStage(stage);
  return prev ? firstStepOfStage(prev) : null;
}

/**
 * Steps whose per-step / per-tool undo material is disposable after export.
 * Everything in the global flow except the three retained stage checkpoints.
 */
export function prunableStepsAfterExport() {
  const retained = new Set(RETAINED_STEPS_AFTER_EXPORT);
  return GLOBAL_STEP_ORDER.filter((step) => !retained.has(step));
}

/** True once a stage's steps are all marked complete. */
export function isStageComplete(stageId, stepCompletion) {
  const steps = STEPS_BY_STAGE[stageId] ?? [];
  return steps.length > 0 && steps.every((s) => !!stepCompletion?.[s]);
}

export { SCAN_PROCESS_STEPS, MAIN_PROCESS_STEPS };
